import { Cardinality, MapLike, PathDirection, PossibleBusinessObject, Traversal, getCardinality } from '@iotv/iotv-v3-types'
import { EdgeAttributeDefinition, EdgeDefinitionType } from './types'
import { ModelClassNames } from '../ModelClassNames'
import { typedEdgeTypeMatrix as edgeTypeMatrix } from './EdgeTypeMatrix'
import { Group } from '../../model'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../util/lambda/debug'
const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

/** 2024 03 Note: EdgeTypeMatrix Mutation
 * The edgeTypeMatrix is mutated by the index of @iotv/datamodel such that the mutated map is exported from @iotv/datamodel-core for all consumers
 * file:///./../../../../datamodel/src/index.ts
 *
 * ie non-core, external project edgeTypes are included. As observered in UI Listerlizer where an import from @iotv/datamodel-core of getParentEdgeTypeId correctly
 * resolves external project edges.
 *
 * Look here if unwanted side effects are seen in the future.
 * Resolution is to move edge validation function to this module.
 */

/** sorts either EdgeTypeDefinition or EdgeAttributeDefinition by cardinality  */
const sortEdgeAttributeDefinitionEntiresByCardinality = (
  entryA: [string, EdgeAttributeDefinition | EdgeDefinitionType],
  entryB: [string, EdgeAttributeDefinition | EdgeDefinitionType],
) => {
  const [ka, va] = entryA
  const [kb, vb] = entryB
  const cardinalityA = getCardinality(va.cardinality?.in ?? null)
  const cardinalityB = getCardinality(vb.cardinality?.in ?? null)
  return cardinalityA === Cardinality.ONE ? -1 : cardinalityB === Cardinality.ONE ? 1 : 0
}

/** returns the first edgeType defined in the edgeTypeMatrix, where parentType is the source ( outV ) and childType is the target ( inV )  */
export const getParentEdgeTypeId = (childType: string, parentType: string): string | undefined => {
  const possibleEdgeDefinitions = Object.values(getAllParentEdgeAttributeDefinitions(childType as ModelClassNames))
  const filteredEdgeDefinitions = possibleEdgeDefinitions.filter((def) => def.objectTypeId === parentType)
  if (filteredEdgeDefinitions.length > 1) {
    debug &&
      console.log(
        `WARN more that one possible edgeTypeId on ${parentType} to ${childType}, ${filteredEdgeDefinitions.map((def) => def.edgeTypeId).join(', ')}. returning first`,
      )
  }
  return filteredEdgeDefinitions?.[0]?.edgeTypeId
}

/**
 * returns the first edgeType defined in the edgeTypeMatrix, where parentType is the source ( outV ) and childType is the target ( inV )
 * This function performs the opposite lookup to getParentEdgeTypeId, and should return the same result when both functions use the same parent and child types
 */
export const getChildEdgeTypeId = (parentType: string, childType: string): string | undefined => {
  const possibleEdgeDefinitions = Object.values(getAllChildEdgeAttributeDefinitions(parentType as ModelClassNames))
  const filteredEdgeDefinitions = possibleEdgeDefinitions.filter((def) => def.objectTypeId === childType)
  if (filteredEdgeDefinitions.length > 1) {
    debug &&
      console.log(
        `WARN more that one possible edgeTypeId on ${parentType} to ${childType}, ${filteredEdgeDefinitions.map((def) => def.edgeTypeId).join(', ')}. returning first`,
      )
  }
  return filteredEdgeDefinitions?.[0]?.edgeTypeId
}

/** 2024 04 returns map of arrays of entries of edgeTypeMatrix where modelTypeIn is child on canonical edgeType, keyed by canonical source - eg in replacement for list generation */
export const getAllParentEdgeTypeMatrixEntries = (modelTypeIn: string): MapLike<EdgeDefinitionType[]> => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>
  let edgeMatrixEntries: MapLike<EdgeDefinitionType[]> = {}
  Object.entries(imposterMatrix).forEach(([modelTypeOut, possibleModelTypeInEntries]) =>
    Object.entries(possibleModelTypeInEntries)
      .filter(([possibleModelTypeIn, _edgeTypeMap]) => modelTypeIn === possibleModelTypeIn)
      .forEach(([_possibleModelTypeIn, edgeTypeMap]) => Object.assign(edgeMatrixEntries, { [modelTypeOut]: Object.values(edgeTypeMap) })),
  )
  return edgeMatrixEntries
}

/** 2024 04 returns map of all entries of edgeTypeMatrix where modelTypeOut is parent on canonical edgeType, keyed by canoical target - eg in replacement for list generation */
export const getAllChildEdgeTypeMatrixEntries = (modelTypeOut: string): MapLike<EdgeDefinitionType[]> => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>
  let edgeMatrixEntries: MapLike<EdgeDefinitionType[]> = {}
  const possibleModelTypeInEntries = imposterMatrix[modelTypeOut] ?? {}
  Object.entries(possibleModelTypeInEntries).forEach(([modelTypeIn, edgeTypeMap]) =>
    Object.assign(edgeMatrixEntries, { [modelTypeIn]: Object.values(edgeTypeMap) }),
  )
  return edgeMatrixEntries
}

export const getTypeAllChildEdgeDefinitionTypes = (modelTypeOut: ModelClassNames, modelTypeIn: ModelClassNames): MapLike<EdgeDefinitionType> => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>

  let edgeDefinitionTypes: MapLike<EdgeDefinitionType> = {}
  if (imposterMatrix[modelTypeOut]) {
    const l1 = imposterMatrix[modelTypeOut]
    if (l1[modelTypeIn]) {
      edgeDefinitionTypes = l1[modelTypeIn]
    }
  }

  return edgeDefinitionTypes
}

export const getKnownEdgeDefinitionType = (modelTypeOut: ModelClassNames, modelTypeIn: ModelClassNames, edgeTypeId: string): EdgeDefinitionType | undefined => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>

  let edgeDefinitionType: EdgeDefinitionType | undefined = undefined

  const allTypeEdgeDefinitionTypes = getTypeAllChildEdgeDefinitionTypes(modelTypeOut, modelTypeIn) //might be able to substitute

  if (imposterMatrix[modelTypeOut]) {
    const l1 = imposterMatrix[modelTypeOut]
    if (l1[modelTypeIn]) {
      const l2 = l1[modelTypeIn]
      if (l2.edgeTypeId) {
        edgeDefinitionType = l2[edgeTypeId]
      }
    }
  }
  return edgeDefinitionType
}

type GetEdgeTypeIdFromTraversalInput = { fromType: string; toType: string; traversal: Traversal }

/** Returns edgeTypeId give a from and to (non canonical direction ) objectTypeId and a Traversal sense */
export const getEdgeTypeIdFromTraversal = ({ fromType, toType, traversal }: GetEdgeTypeIdFromTraversalInput) => {
  switch (traversal) {
    case Traversal.ASCENDING:
      return getChildEdgeTypeId(toType, fromType)
    case Traversal.DESCENDING:
      return getChildEdgeTypeId(fromType, toType)
  }
}

/** EDGE ATTRIBUTE DEFINITION FNS - THESE ARE USED AS A SHIM TO WORK WITH THE LEGACY METHOD OF GENERATING LISTS FROM object.getUserAttribues in UI - UNDER DEPRECATION */

/** given an edgeDefinitionType entry from the edgeTypeMatix, plus the modelTypeOut ( canonical source ) and modelTypeIn ( canonical target ) returns a object of type EdgeAttributeDefinition which is compatible with the form used in UI ( AttributeDefinition ) where it is used to automatically generate ViewDefinitions
 * In this object, objectTypeId and sourceObjectType have the following meaning:
 *  - objectTypeId: the type of related object relative to the page context object
 *  - sourceObjectType: the type of the page context object
 *
 *  - modelTypeOut is canonical source ( outV )
 *  - modelTypeIn is the canonical target ( inV )
 */
export const getEdgeAttributeDefintion = (
  modelTypeOut: ModelClassNames,
  modelTypeIn: ModelClassNames,
  edgeDefinitionType: EdgeDefinitionType,
  direction: PathDirection,
): EdgeAttributeDefinition => {
  const attDef: Partial<EdgeAttributeDefinition> & Pick<EdgeAttributeDefinition, 'type'> = {
    type: 'RelatedType',
    edgeTypeId: edgeDefinitionType.edgeTypeId,
    cardinality: edgeDefinitionType.cardinality,
    editable: true,
    direction,
  }

  if (direction === PathDirection.child) {
    attDef.label = edgeDefinitionType.labels.out
    ;(attDef.objectTypeId = modelTypeIn), (attDef.sourceObjectType = modelTypeOut)
  } else if (direction === PathDirection.parent) {
    attDef.label = edgeDefinitionType.labels.in
    ;(attDef.objectTypeId = modelTypeOut), (attDef.sourceObjectType = modelTypeIn)
  } else {
    attDef.label = edgeDefinitionType.labels.out
    ;(attDef.objectTypeId = modelTypeIn), (attDef.sourceObjectType = modelTypeOut)
  }
  return attDef as EdgeAttributeDefinition
}

export const getKnownEdgeAttributeDefinition = (
  modelTypeOut: ModelClassNames,
  modelTypeIn: ModelClassNames,
  edgeTypeId: string,
  direction: PathDirection,
): EdgeAttributeDefinition | undefined => {
  let edgeDefinitionType: EdgeDefinitionType | undefined = getKnownEdgeDefinitionType(modelTypeOut, modelTypeIn, edgeTypeId)
  return edgeDefinitionType ? getEdgeAttributeDefintion(modelTypeOut, modelTypeIn, edgeDefinitionType, direction) : undefined
}

/** For a given objectTypeId (modelTypeOut) returns all possible Parent->Child ( outV -> inV ) edgeAttributeDefinitions, ie where the objectTypeId is defined as the target ( inV, Child )
 * use this if its needed to find the correct edgeTypeId between modelType in and a Parent / outV / source objectType ie the reverse of the canonical definition in the edgeTypeMatrix
 */
export const getAllParentEdgeAttributeDefinitions = (modelTypeIn: ModelClassNames): MapLike<EdgeAttributeDefinition> => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>
  let edgeAttribtueDefinition: MapLike<EdgeAttributeDefinition> = {}

  Object.entries(imposterMatrix).forEach(([modelTypeOut, possibleModelTypeInEntries]) =>
    Object.entries(possibleModelTypeInEntries)
      .filter(([possibleModelTypeIn, _edgeTypeMap]) => modelTypeIn === possibleModelTypeIn)
      .forEach(([_possibleModelTypeIn, edgeTypeMap]) =>
        Object.entries(edgeTypeMap).forEach(([edgeKey, edgeTypeDefinition]) =>
          Object.assign(edgeAttribtueDefinition, {
            [`${modelTypeIn}_in_${edgeKey}_${modelTypeOut}`]: getEdgeAttributeDefintion(
              modelTypeOut as ModelClassNames,
              modelTypeIn,
              edgeTypeDefinition,
              PathDirection.parent,
            ),
          }),
        ),
      ),
  )

  return Object.fromEntries(Object.entries(edgeAttribtueDefinition).sort(sortEdgeAttributeDefinitionEntiresByCardinality))
}

/** For a given objectTypeId (modelTypeOut) returns all possible Parent->Child ( outV -> inV ) edgeAttributeDefinitions, ie where the objectTypeId is defined as the source ( outV, Parent ) */
export const getAllChildEdgeAttributeDefinitions = (modelTypeOut: ModelClassNames): MapLike<EdgeAttributeDefinition> => {
  const imposterMatrix = edgeTypeMatrix as MapLike<MapLike<MapLike<EdgeDefinitionType>>>
  let edgeAttribtueDefinition: MapLike<EdgeAttributeDefinition> = {}
  const possibleModelTypeInEntries = imposterMatrix[modelTypeOut] ?? {}

  Object.entries(possibleModelTypeInEntries).forEach(([modelTypeIn, edgeTypeMap]) =>
    Object.entries(edgeTypeMap).forEach(([edgeKey, edgeTypeDefinition]) =>
      Object.assign(edgeAttribtueDefinition, {
        [`${modelTypeOut}_out_${edgeKey}_${modelTypeIn}`]: getEdgeAttributeDefintion(
          modelTypeOut,
          modelTypeIn as ModelClassNames,
          edgeTypeDefinition,
          PathDirection.child,
        ),
      }),
    ),
  )

  return Object.fromEntries(Object.entries(edgeAttribtueDefinition).sort(sortEdgeAttributeDefinitionEntiresByCardinality))
}

/** For a given objectTypeId (modelTypeOut) returns all possible edgeAttributeDefinitions where the objectTypeId is either the source ( outV, Parent ) or target (inV, Child ) */
export const getAllEdgeDefinitionTypes = (modelType: ModelClassNames): MapLike<EdgeAttributeDefinition> =>
  Object.assign(getAllParentEdgeAttributeDefinitions(modelType), getAllChildEdgeAttributeDefinitions(modelType))

/** For a given object (possibleBusinessObject) returns all possible edgeAttributeDefinitions where the objectTypeId is either the source ( outV, Parent ) or target (inV, Child ), takes into account the special case of groups where the supportedType determines which edgeType is used */
export const getAllEdgeDefinitionTypesForObject = (possibleBusinessObject: PossibleBusinessObject) => {
  let edgeDefinitions: MapLike<EdgeAttributeDefinition> = getAllEdgeDefinitionTypes(possibleBusinessObject.type as ModelClassNames)
  let deviceEnabledTypeEdgeDefinitions: MapLike<EdgeAttributeDefinition> | undefined = undefined

  if (possibleBusinessObject.type === 'Group') {
    console.log('Group edgeDefinitions', edgeDefinitions)

    /** 2024 04 moved from UI, removes group subtypes where type is not same as possibleBusinessObject.supportedType  */
    edgeDefinitions = Object.fromEntries(
      Object.entries(edgeDefinitions).filter(([k, v]) => {
        return (
          v.edgeTypeId?.toLocaleLowerCase().includes((possibleBusinessObject as Group).supportedType?.toLowerCase() ?? 'never') ||
          v.edgeTypeId?.includes(edgeTypeMatrix.Group?.Group?.group_has_subGroup.edgeTypeId ?? 'never')
        )
      }),
    )

    /* REMOVED 2024 04 replaced by above
        deviceEnabledTypeEdgeDefinitions = Object.fromEntries(Object.entries(edgeDefinitions).filter(([ k, v ]) => {
            ** this predicate that the edgeTypeId of a group to entity type edge must have the supportedType in the edge name is
             * correct but very fragile
             *
            return v.edgeTypeId?.toLocaleLowerCase().includes((possibleBusinessObject as Group).supportedType?.toLowerCase() ?? 'never') || v.edgeTypeId === (edgeTypeMatrix.Group?.Group?.group_has_subGroup?.edgeTypeId ?? 'never')
        }))
        */
  }
  return { /*...deviceEnabledTypeEdgeDefinitions,*/ ...edgeDefinitions }
}
